import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta, mxn } from '../formato';
import { usePuede } from '../permisos';
import type { Vehiculo } from '../paginas/tipos';

interface AnticipoCliente {
  estado: 'pendiente' | 'reportado' | 'pagado' | 'devuelto' | 'aplicado';
  totalMxn: string;
  saldoMxn: string;
  referencia: string | null;
  pagadoEn: string | null;
  desglose: {
    pujaMaximaUsd: string;
    comisionMxn: string;
    gastosMxn: string;
    importacionMxn: string;
  };
  movimientos: { fecha: string; tipo: string; montoMxn: string; nota: string | null }[];
  instrucciones: {
    metodo: string;
    cuenta: { banco: string; clabe: string; titular: string } | null;
    referencia: string;
    totalMxn: string;
    nota: string;
  } | null;
}
interface Seguimiento {
  pujas: {
    id: string;
    subasta: string;
    numeroLote: string;
    pujaMaximaUsd: string;
    resultado: string;
    precioMartilloUsd: string | null;
    resueltoEn: string | null;
  }[];
  importacion: {
    vehiculoId: string | null;
    vin: string | null;
    tipoImportacion: string | null;
    aduanaEntrada: string | null;
    pasos: Record<string, { hecho: boolean; fecha: string | null }>;
    pedimento: { numero: string; fecha: string } | null;
    entregadoEn: string | null;
  } | null;
}
interface Liquidacion {
  estado: 'propuesta' | 'cerrada';
  anticipoMxn: string;
  totalRealMxn: string;
  diferenciaMxn: string;
  decision: 'devolver' | 'aplicar' | null;
  costos: { concepto: string; cantidad: string; moneda: string; etapa: string }[];
}
const usd = (c: string | null) =>
  c ? `USD ${Number(c).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '—';
const PASOS: Record<string, string> = {
  compra: 'Compra',
  titulo: 'Título en mano',
  retiro: 'Retiro del patio',
  circulacion_temporal: 'Circulación temporal',
  traslado: 'Traslado a la aduana',
  despacho: 'Pedimento',
  cruce: 'Cruce',
  recepcion: 'Recepción',
  emplacado: 'Emplacado',
};

/**
 * Del anticipo a la liquidación, lado del cliente (arquitectura 2.15, pasos 5 a 8): el anticipo
 * es su dinero en custodia; Siniestrados puja solo con el anticipo pagado y nunca por arriba de
 * la puja máxima que aprobó; al ganar, el auto entra a su inventario; al final decide si la
 * diferencia se le devuelve o se aplica a otro encargo.
 */
export function AnticipoEncargo({ encargoId, estado }: { encargoId: string; estado: string }) {
  const api = useApi();
  const puede = usePuede();
  const activo = ![
    'solicitado',
    'en_busqueda',
    'propuesta_enviada',
    'rechazado',
    'cancelado',
  ].includes(estado);
  const anticipo = useConsulta<AnticipoCliente>(
    activo ? `/sourcing/encargos/${encargoId}/anticipo` : null,
  );
  const seguimiento = useConsulta<Seguimiento>(
    activo ? `/sourcing/encargos/${encargoId}/seguimiento` : null,
  );
  const liquidacion = useConsulta<Liquidacion>(
    ['entregado', 'perdido', 'liquidado'].includes(estado)
      ? `/sourcing/encargos/${encargoId}/liquidacion`
      : null,
  );
  const vehiculos = useConsulta<Vehiculo[]>(
    seguimiento.datos?.importacion && !seguimiento.datos.importacion.vehiculoId
      ? '/inventory/vehiculos'
      : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const hacer = async (ruta: string, cuerpo: object, mensaje: string) => {
    setError(null);
    setAviso(null);
    try {
      await api('POST', `/sourcing/encargos/${encargoId}/${ruta}`, cuerpo);
      setAviso(mensaje);
      anticipo.recargar();
      seguimiento.recargar();
      liquidacion.recargar();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const reportar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const referencia = String(new FormData(e.currentTarget).get('referencia') ?? '').trim();
    if (referencia)
      void hacer(
        'anticipo/reporte',
        { referencia },
        'Aviso enviado: Tesorería concilia el depósito',
      );
  };
  if (!activo) return null;
  if (anticipo.estadoError === 503)
    return (
      <section className="seccion">
        <header>
          <h2>Anticipo en custodia</h2>
        </header>
        <p className="vacio">{anticipo.error}</p>
      </section>
    );
  const a = anticipo.datos;
  const s = seguimiento.datos;
  const liq = liquidacion.datos;
  const imp = s?.importacion ?? null;
  const puedeDecidir = puede('encargos.decidir');
  return (
    <>
      <section className="seccion">
        <header>
          <h2>Anticipo en custodia</h2>
          {a && (
            <span
              className={`etiqueta ${a.estado === 'pagado' ? 'exito' : a.estado === 'reportado' ? 'aviso' : ''}`}
            >
              {a.estado}
            </span>
          )}
        </header>
        {anticipo.error && anticipo.estadoError !== 404 && (
          <p className="error" style={{ margin: 16 }}>
            {anticipo.error}
          </p>
        )}
        {error && (
          <p className="error" style={{ margin: 16 }}>
            {error}
          </p>
        )}
        {aviso && (
          <p className="etiqueta exito" style={{ margin: 16 }}>
            {aviso}
          </p>
        )}
        {a && (
          <div className="cuerpo" style={{ display: 'grid', gap: 12 }}>
            <div className="tarjetas">
              <div className="tarjeta">
                <div className="rotulo">Anticipo</div>
                <div className="cifra">{mxn(a.totalMxn)}</div>
                <div className="nota">
                  Puja máxima {usd(a.desglose.pujaMaximaUsd)} + cargos, traslado, importación,
                  comisión {mxn(a.desglose.comisionMxn)} y gastos {mxn(a.desglose.gastosMxn)}
                </div>
              </div>
              <div className="tarjeta">
                <div className="rotulo">En custodia</div>
                <div className="cifra">{mxn(a.saldoMxn)}</div>
                <div className="nota">Dinero tuyo en cuenta separada, con contrato de mandato</div>
              </div>
            </div>
            {a.instrucciones && (
              <div className="tarjeta" style={{ display: 'grid', gap: 6 }}>
                <strong>Cómo pagar</strong>
                <span>{a.instrucciones.nota}</span>
                {a.instrucciones.cuenta && (
                  <span className="vin">
                    {a.instrucciones.cuenta.banco} · CLABE {a.instrucciones.cuenta.clabe} ·{' '}
                    {a.instrucciones.cuenta.titular} · referencia {a.instrucciones.referencia}
                  </span>
                )}
                {puedeDecidir && (a.estado === 'pendiente' || a.estado === 'reportado') && (
                  <form className="acciones" onSubmit={reportar}>
                    <input
                      name="referencia"
                      required
                      maxLength={80}
                      placeholder="Referencia o clave de rastreo de tu transferencia"
                      defaultValue={a.referencia ?? ''}
                      style={{ flex: 1, minWidth: 220 }}
                    />
                    <button className="boton primario">
                      {a.estado === 'reportado' ? 'Actualizar aviso' : 'Ya pagué'}
                    </button>
                  </form>
                )}
              </div>
            )}
            {a.movimientos.length > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Movimiento</th>
                    <th className="num">Monto</th>
                    <th>Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {a.movimientos.map((m, i) => (
                    <tr key={i}>
                      <td>{fechaCorta(m.fecha)}</td>
                      <td>{m.tipo}</td>
                      <td className="num">{mxn(m.montoMxn)}</td>
                      <td>{m.nota ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>
      {s && (s.pujas.length > 0 || imp) && (
        <section className="seccion">
          <header>
            <h2>Puja e importación</h2>
          </header>
          <div className="cuerpo" style={{ display: 'grid', gap: 12 }}>
            {s.pujas.map((p) => (
              <div key={p.id}>
                <span
                  className={`etiqueta ${p.resultado === 'ganada' ? 'exito' : p.resultado === 'pendiente' ? 'aviso' : ''}`}
                >
                  {p.resultado}
                </span>{' '}
                {p.subasta} · lote {p.numeroLote} · puja máxima {usd(p.pujaMaximaUsd)}
                {p.precioMartilloUsd ? ` · martillo ${usd(p.precioMartilloUsd)}` : ''}
                {p.resueltoEn ? ` · ${fechaCorta(p.resueltoEn)}` : ''}
              </div>
            ))}
            {imp && (
              <>
                <div className="subtitulo">
                  Importación {imp.tipoImportacion ?? ''} por la aduana de{' '}
                  {imp.aduanaEntrada ?? '—'} · VIN {imp.vin ?? '—'}
                </div>
                <div className="acciones" style={{ flexWrap: 'wrap' }}>
                  {Object.entries(PASOS).map(([clave, nombre]) => (
                    <span
                      key={clave}
                      className={`etiqueta ${imp.pasos[clave]?.hecho ? 'exito' : ''}`}
                    >
                      {nombre}
                      {imp.pasos[clave]?.fecha
                        ? ` · ${fechaCorta(imp.pasos[clave]?.fecha ?? '')}`
                        : ''}
                    </span>
                  ))}
                </div>
                {imp.vehiculoId ? (
                  <p className="subtitulo">
                    El auto está en tu inventario:{' '}
                    <Link to={`/vehiculos/${imp.vehiculoId}`}>ver vehículo</Link>
                    {imp.entregadoEn ? ` · entregado el ${fechaCorta(imp.entregadoEn)}` : ''}
                  </p>
                ) : (
                  puedeDecidir && (
                    <div className="acciones" style={{ flexWrap: 'wrap' }}>
                      <span className="subtitulo">
                        Da de alta el auto en Vehículos con el VIN {imp.vin ?? ''} y lígalo aquí:
                      </span>
                      <select
                        aria-label="Vehículo"
                        onChange={(e) =>
                          e.target.value &&
                          void hacer(
                            'vehiculo',
                            { vehiculoId: e.target.value },
                            'Vehículo ligado al encargo',
                          )
                        }
                        defaultValue=""
                      >
                        <option value="">Elegir vehículo…</option>
                        {(vehiculos.datos ?? [])
                          .filter((v) => !imp.vin || v.vin === imp.vin)
                          .map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.vin}
                            </option>
                          ))}
                      </select>
                      <Link className="boton" to="/vehiculos/nuevo">
                        Registrar vehículo
                      </Link>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </section>
      )}
      {liq && (
        <section className="seccion">
          <header>
            <h2>Liquidación</h2>
            <span className="etiqueta">{liq.estado}</span>
          </header>
          <div className="cuerpo" style={{ display: 'grid', gap: 12 }}>
            <div className="tarjetas">
              <div className="tarjeta">
                <div className="rotulo">Anticipo en custodia</div>
                <div className="cifra">{mxn(liq.anticipoMxn)}</div>
              </div>
              <div className="tarjeta">
                <div className="rotulo">Costos reales, comisión y gastos</div>
                <div className="cifra">{mxn(liq.totalRealMxn)}</div>
              </div>
              <div className="tarjeta">
                <div className="rotulo">Diferencia a tu favor</div>
                <div className="cifra">{mxn(liq.diferenciaMxn)}</div>
              </div>
            </div>
            <table>
              <tbody>
                {liq.costos.map((c, i) => (
                  <tr key={i}>
                    <td>{c.concepto}</td>
                    <td>{c.etapa}</td>
                    <td className="num">
                      {c.moneda === 'USD' ? usd(c.cantidad) : mxn(c.cantidad)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {liq.estado === 'propuesta' && puedeDecidir && (
              <div className="acciones">
                <button
                  className={`boton ${liq.decision === 'devolver' ? 'primario' : ''}`}
                  onClick={() =>
                    void hacer(
                      'liquidacion/decision',
                      { decision: 'devolver' },
                      'Elegiste que te devuelvan la diferencia',
                    )
                  }
                >
                  Devolverme la diferencia
                </button>
                <button
                  className={`boton ${liq.decision === 'aplicar' ? 'primario' : ''}`}
                  onClick={() =>
                    void hacer(
                      'liquidacion/decision',
                      { decision: 'aplicar' },
                      'Elegiste aplicar la diferencia a otro encargo',
                    )
                  }
                >
                  Aplicarla a otro encargo
                </button>
              </div>
            )}
            {liq.decision && (
              <p className="subtitulo">
                Decisión:{' '}
                {liq.decision === 'devolver'
                  ? 'devolver la diferencia'
                  : 'aplicarla a otro encargo'}
                {liq.estado === 'cerrada' ? ' · cerrada por Tesorería' : ' · Tesorería la cierra'}
              </p>
            )}
          </div>
        </section>
      )}
    </>
  );
}
